import React from "react";
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Divider,
  Link,
  Icon,
  alpha,
  useTheme,
  Button,
  Chip,
} from "@mui/material";
import { IESDoc } from "../../../../app.interface";
import dayjs from "dayjs";
import { useAuth } from "../../../auth/hooks/useAuth";

interface Contact {
  id?: string;
  name?: string;
  email?: string;
  phone?: string;
  whatsapp?: string;
}

interface EntityDetailsDrawerProps {
  open: boolean;
  onClose: () => void;
  entity: IESDoc | null;
  entityType: "supplier" | "warehouse" | "department" | "staff";
  onEdit?: (entity: IESDoc) => void;
  onDelete?: (entity: IESDoc) => void;
}

export const EntityDetailsDrawer: React.FC<EntityDetailsDrawerProps> = ({
  open,
  onClose,
  entity,
  entityType,
  onEdit,
  onDelete,
}) => {
  const theme = useTheme();
  const { user } = useAuth();
  console.log(user);
  if (!entity) return null;

  const getEntityIcon = () => {
    switch (entityType) {
      case "supplier":
        return "business";
      case "warehouse":
        return "warehouse";
      case "department":
        return "domain";
      case "staff":
        return "person";
      default:
        return "business";
    }
  };

  const getEntityTitle = () => {
    switch (entityType) {
      case "supplier":
        return "Supplier Details";
      case "warehouse":
        return "Warehouse Details";
      case "department":
        return "Department Details";
      case "staff":
        return "Staff Details";
      default:
        return "Details";
    }
  };

  const renderStaffDetails = () => {
    return (
      <Box sx={{ p: 3 }}>
        <Box display="grid" gap={3}>
          <DetailItem
            icon="person"
            label="Full Name"
            value={`${entity.first_name} ${entity.last_name}`}
          />
          <DetailItem
            icon="email"
            label="Email"
            value={entity.email}
            isLink
            linkType="mailto"
          />
          <DetailItem
            icon="phone"
            label="Phone Number"
            value={entity.phone_number || "N/A"}
            isLink
            linkType="tel"
          />
          <DetailItem
            icon="badge"
            label="Role"
            value={entity.role}
            chip={{
              color: "#0891B2",
              icon: "badge",
            }}
          />
          <DetailItem
            icon="fiber_manual_record"
            label="Status"
            value={entity.is_active ? "Active" : "Inactive"}
            valueColor={entity.is_active ? "#059669" : "#DC2626"}
          />
          <DetailItem
            icon="schedule"
            label="Created At"
            value={dayjs(entity.created_at).format("MMM D, YYYY")}
          />
          <DetailItem
            icon="person"
            label="Created By"
            value={entity.created_by_email || "N/A"}
          />
        </Box>

        {/* Assignment Details */}
        {entity.assignment_details && (
          <>
            <Divider sx={{ my: 3, borderColor: alpha("#0891B2", 0.1) }} />
            <Typography
              variant="subtitle2"
              sx={{
                mb: 2,
                color: "text.primary",
                fontFamily: "Montserrat",
                fontWeight: 600,
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <Icon sx={{ fontSize: 20 }}>location_on</Icon>
              Assignment Information
            </Typography>

            <Box
              sx={{
                p: 2,
                borderRadius: 1,
                bgcolor: alpha("#0891B2", 0.04),
                border: "1px solid",
                borderColor: alpha("#0891B2", 0.1),
              }}
            >
              <DetailItem
                icon="business"
                label="Assigned To"
                value={entity.assignment_details.assignment_name}
              />
              <Box sx={{ mt: 1 }}>
                <Chip
                  size="small"
                  icon={<Icon sx={{ fontSize: 16 }}>category</Icon>}
                  label={entity.assignment_details.index
                    .replace("pos_", "")
                    .replace("_", " ")}
                  sx={{
                    bgcolor: alpha("#0891B2", 0.1),
                    color: "#0891B2",
                    fontWeight: 500,
                  }}
                />
              </Box>
            </Box>
          </>
        )}

        {/* POS Details */}
        {entity.is_pos_user && user?.is_store_owner ? (
          <>
            <Divider sx={{ my: 3, borderColor: alpha("#0891B2", 0.1) }} />
            <Typography
              variant="subtitle2"
              sx={{
                mb: 2,
                color: "text.primary",
                fontFamily: "Montserrat",
                fontWeight: 600,
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <Icon sx={{ fontSize: 20 }}>point_of_sale</Icon>
              POS Information
            </Typography>

            <Box
              sx={{
                p: 2,
                borderRadius: 1,
                bgcolor: alpha("#0891B2", 0.04),
                border: "1px solid",
                borderColor: alpha("#0891B2", 0.1),
              }}
            >
              <DetailItem
                icon="pin"
                label="POS PIN"
                value={entity.pos_pin || "N/A"}
              />
              <Box sx={{ mt: 2 }} display="flex" gap={1}>
                {entity.is_pos_supervisor && (
                  <Chip
                    size="small"
                    icon={<Icon sx={{ fontSize: 16 }}>supervisor_account</Icon>}
                    label="POS Supervisor"
                    sx={{
                      bgcolor: alpha("#0891B2", 0.1),
                      color: "#0891B2",
                      fontWeight: 500,
                    }}
                  />
                )}
                <Chip
                  size="small"
                  icon={<Icon sx={{ fontSize: 16 }}>point_of_sale</Icon>}
                  label="POS User"
                  sx={{
                    bgcolor: alpha("#0891B2", 0.1),
                    color: "#0891B2",
                    fontWeight: 500,
                  }}
                />
              </Box>
            </Box>
          </>
        ) : null}

        {/* Action Buttons */}
        {(onEdit || onDelete) && (
          <>
            <Divider sx={{ my: 3, borderColor: alpha("#0891B2", 0.1) }} />
            <Box display="flex" gap={2}>
              {onEdit && (
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<Icon>edit</Icon>}
                  onClick={() => onEdit(entity)}
                  sx={{
                    borderColor: "#0891B2",
                    color: "#0891B2",
                    "&:hover": {
                      borderColor: "#0E7490",
                      bgcolor: alpha("#0891B2", 0.04),
                    },
                  }}
                >
                  Edit Staff
                </Button>
              )}
              {onDelete && (
                <Button
                  fullWidth
                  variant="outlined"
                  color="error"
                  startIcon={<Icon>delete</Icon>}
                  onClick={() => onDelete(entity)}
                  sx={{
                    borderColor: "#DC2626",
                    color: "#DC2626",
                    "&:hover": {
                      borderColor: "#B91C1C",
                      bgcolor: alpha("#DC2626", 0.04),
                    },
                  }}
                >
                  Delete Staff
                </Button>
              )}
            </Box>
          </>
        )}
      </Box>
    );
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: 400,
          maxWidth: "100%",
          borderTopLeftRadius: 8,
          borderBottomLeftRadius: 8,
          overflow: "hidden",
          borderLeft: "1px solid",
          borderColor: alpha("#0891B2", 0.1),
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      <Box
        sx={{
          px: 3,
          py: 2,
          background: "#0891B2",
          color: "white",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
          flexShrink: 0,
        }}
      >
        <Box display="flex" alignItems="center" gap={1.5}>
          <Icon>{getEntityIcon()}</Icon>
          <Typography variant="subtitle1" fontWeight={600}>
            {getEntityTitle()}
          </Typography>
        </Box>
        <IconButton
          onClick={onClose}
          size="small"
          sx={{
            color: "white",
            "&:hover": {
              bgcolor: "rgba(255,255,255,0.1)",
            },
          }}
        >
          <Icon>close</Icon>
        </IconButton>
      </Box>

      <Box
        sx={{
          flex: 1,
          overflow: "auto",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {entityType === "staff" ? renderStaffDetails() : null}
      </Box>
    </Drawer>
  );
};

const DetailItem: React.FC<{
  icon: string;
  label: string;
  value: string;
  valueColor?: string;
  isLink?: boolean;
  linkType?: "mailto" | "tel" | "url";
  chip?: {
    color: string;
    icon: string;
  };
}> = ({ icon, label, value, valueColor, isLink, linkType, chip }) => (
  <Box>
    <Typography
      variant="caption"
      sx={{
        color: "text.secondary",
        display: "flex",
        alignItems: "center",
        gap: 1,
        mb: 0.5,
        fontFamily: "Montserrat",
      }}
    >
      <Icon sx={{ fontSize: 16 }}>{icon}</Icon>
      {label}
    </Typography>
    {chip ? (
      <Chip
        icon={<Icon sx={{ fontSize: 16 }}>{chip.icon}</Icon>}
        label={value}
        size="small"
        sx={{
          bgcolor: alpha(chip.color, 0.1),
          color: chip.color,
          fontWeight: 500,
        }}
      />
    ) : isLink ? (
      <Typography
        variant="body1"
        component="a"
        href={`${
          linkType === "mailto" ? "mailto:" : linkType === "tel" ? "tel:" : ""
        }${value}`}
        target={linkType === "url" ? "_blank" : undefined}
        rel={linkType === "url" ? "noopener noreferrer" : undefined}
        sx={{
          fontWeight: 500,
          color: "#0891B2",
          fontFamily: "Montserrat",
          textDecoration: "none",
          display: "block",
          "&:hover": {
            textDecoration: "underline",
          },
        }}
      >
        {value}
      </Typography>
    ) : (
      <Typography
        variant="body1"
        sx={{
          fontWeight: 500,
          color: valueColor || "text.primary",
          fontFamily: "Montserrat",
        }}
      >
        {value}
      </Typography>
    )}
  </Box>
);
